'use client';

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { Partners } from '@/components/partners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCog, faUsers } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  jobTitle: string;
  businessType: string;
  cameraCount: string;
  currentSolution: string;
  message: string;
  agreeTerms: boolean;
  subscribe: boolean;
}

export default function ContactSalesPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [formData, setFormData] = React.useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    jobTitle: '',
    businessType: '',
    cameraCount: '',
    currentSolution: '',
    message: '',
    agreeTerms: false,
    subscribe: true
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [duplicate, setDuplicate] = React.useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (submitted || duplicate) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submitted, duplicate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitted(false);
    setDuplicate(false);

    try {
      // Build message with optional fields
      const extraLines: string[] = [];
      if (formData.jobTitle) extraLines.push(`Job title: ${formData.jobTitle}`);
      if (formData.businessType) extraLines.push(`Business type: ${formData.businessType}`);
      if (formData.cameraCount) extraLines.push(`Camera count: ${formData.cameraCount}`);
      if (formData.currentSolution) extraLines.push(`Current solution: ${formData.currentSolution}`);
      const message = [formData.message, ...extraLines].filter(Boolean).join('\n');

      const contactData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        company: formData.company.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: message.trim(),
      };

      console.log('Submitting contact form:', contactData);

      // Submit contact form
      const contactResponse = await axios.post(
        `${API_BASE_URL}/contact`,
        contactData,
        {
          headers: { 'Content-Type': 'application/json' },
          validateStatus: (status) => status < 500 // Allow 4xx responses
        }
      );

      console.log('Contact API response:', contactResponse.status, contactResponse.data);

      // Handle duplicate email
      if (contactResponse.status === 409 || contactResponse.data?.error === 'email_exists') {
        setDuplicate(true);
        toast({
          title: t('contactForm.toast.submissionError', 'Submission Error'),
          description: contactResponse.data?.message || t('contactForm.toast.emailExists', 'You have already submitted a request with this email.'),
          variant: 'destructive',
        });
        return;
      }

      // Handle validation errors (400)
      if (contactResponse.status >= 400 && contactResponse.status < 500) {
        const field = contactResponse.data?.field;
        const fieldLabel = field ? t(`contactSales.form.${field}`) : '';
        const msg = field 
          ? `${fieldLabel} is required`
          : contactResponse.data?.message || 'Please check all required fields.';
        
        toast({
          title: 'Validation Error',
          description: msg,
          variant: 'destructive',
        });
        return;
      }

      // Success
      if (!contactResponse.data?.ok) {
        throw new Error('Invalid response from server');
      }

      toast({
        title: t('contactForm.toast.requestSubmitted', 'Request Submitted'),
        description: t('contactForm.toast.requestSubmittedDesc', 'Our team will get back to you shortly.'),
      });

      // Optional: Subscribe to newsletter (non-blocking)
      if (formData.subscribe) {
        try {
          await axios.post(`${API_BASE_URL}/newsletter`, {
            email: formData.email.trim(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            company: formData.company.trim(),
            phone: formData.phone.trim(),
            language: i18n.language,
            source: 'contact-sales',
            tags: ['website', 'contact-sales'],
          }, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000,
          });
        } catch (err) {
          console.warn('Newsletter subscription failed (non-critical):', err);
          // Don't show error — it's optional
        }
      }

      setSubmitted(true);

    } catch (error: any) {
      console.error('Unexpected submission error:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'France', 'Germany', 'Spain', 'Italy', 'Netherlands', 'Other'
  ];

  const businessTypes = [
    { value: 'retail', label: t('contactSales.businessTypes.retail') },
    { value: 'healthcare', label: t('contactSales.businessTypes.healthcare') },
    { value: 'education', label: t('contactSales.businessTypes.education') },
    { value: 'manufacturing', label: t('contactSales.businessTypes.manufacturing') },
    { value: 'hospitality', label: t('contactSales.businessTypes.hospitality') },
    { value: 'transportation', label: t('contactSales.businessTypes.transportation') },
    { value: 'government', label: t('contactSales.businessTypes.government') },
    { value: 'other', label: t('contactSales.businessTypes.other') }
  ];

  const cameraRanges = [
    { value: '1-10', label: '1-10 cameras' },
    { value: '11-50', label: '11-50 cameras' },
    { value: '51-100', label: '51-100 cameras' },
    { value: '101-500', label: '101-500 cameras' },
    { value: '500+', label: '500+ cameras' }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-32 md:py-40 lg:py-48 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-black/40" />
            <video
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            >
              <source src="/oleyes_banner_video_2.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide text-white">
                {t('contactSales.title', 'Contact Our AI Security Experts')}
              </h1>
              <p className="mt-3 sm:mt-5 text-lg sm:text-2xl md:text-3xl font-korolev font-medium text-gray-100 max-w-2xl mx-auto">
                {t('contactSales.subtitle', 'Get personalized solutions, custom pricing, and dedicated support for your security needs')}
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12">
          <div className="py-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faCalendarAlt} className="h-8 w-8 text-slate-700 dark:text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {t('contactSales.features.demo', 'Free Demo')}
              </h3>
              <p className="text-muted-foreground">
                {t('contactSales.features.demoDesc', 'See our AI in action with a personalized demonstration')}
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faCog} className="h-8 w-8 text-slate-700 dark:text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {t('contactSales.features.custom', 'Custom Solutions')}
              </h3>
              <p className="text-muted-foreground">
                {t('contactSales.features.customDesc', 'Tailored security solutions for your specific needs')}
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={faUsers} className="h-8 w-8 text-slate-700 dark:text-slate-300" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {t('contactSales.features.support', 'Expert Support')}
              </h3>
              <p className="text-muted-foreground">
                {t('contactSales.features.supportDesc', 'Dedicated support from our security specialists')}
              </p>
            </div>
          </div>

          {/* Form or Result */}
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {submitted ? (
              <div ref={panelRef} className="bg-card rounded-xl shadow-lg p-8 text-center">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <span className="text-2xl">Checkmark</span>
                </div>
                <h2 className="text-2xl font-semibold mb-2">
                  {t('contactForm.toast.requestSubmitted', 'Request Submitted')}
                </h2>
                <p className="text-muted-foreground">
                  {t('contactForm.toast.requestSubmittedDesc', 'Our team will get back to you shortly.')}
                </p>
              </div>
            ) : duplicate ? (
              <div ref={panelRef} className="bg-card rounded-xl shadow-lg p-8 text-center">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <span className="text-2xl">Warning</span>
                </div>
                <h2 className="text-2xl font-semibold mb-2">
                  {t('contactForm.toast.submissionError', 'Already Submitted')}
                </h2>
                <p className="text-muted-foreground">
                  {t('contactForm.toast.emailExists', 'You have already submitted a request with this email.')}
                </p>
              </div>
            ) : (
              <div className="bg-card rounded-xl shadow-lg p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('contactSales.form.firstName')} *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('contactSales.form.lastName')} *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contactSales.form.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">{t('contactSales.form.company')} *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle">{t('contactSales.form.jobTitle')}</Label>
                      <Input
                        id="jobTitle"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('contactSales.form.phone')} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">{t('contactSales.form.country')} *</Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('contactSales.form.selectCountry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="businessType">{t('contactSales.form.businessType')}</Label>
                      <Select
                        value={formData.businessType}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, businessType: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('contactSales.form.selectBusinessType')} />
                        </SelectTrigger>
                        <SelectContent>
                          {businessTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cameraCount">{t('contactSales.form.cameraCount')}</Label>
                      <Select
                        value={formData.cameraCount}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, cameraCount: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('contactSales.form.selectCameraCount')} />
                        </SelectTrigger>
                        <SelectContent>
                          {cameraRanges.map((range) => (
                            <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currentSolution">{t('contactSales.form.currentSolution')}</Label>
                    <Input
                      id="currentSolution"
                      name="currentSolution"
                      value={formData.currentSolution}
                      onChange={handleChange}
                      placeholder={t('contactSales.form.currentSolutionPlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contactSales.form.message')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contactSales.form.messagePlaceholder')}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="agreeTerms"
                        checked={formData.agreeTerms}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeTerms: checked as boolean }))}
                        required
                      />
                      <label htmlFor="agreeTerms" className="text-sm font-medium leading-none">
                        {t('contactSales.form.agreeToTerms')} *
                      </label>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="subscribe"
                        checked={formData.subscribe}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, subscribe: checked as boolean }))}
                      />
                      <label htmlFor="subscribe" className="text-sm font-medium leading-none">
                        {t('contactSales.form.subscribeToNewsletter')}
                      </label>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-center">
                    <Button type="submit" size="lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : t('contactSales.form.submitButton')}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </section>
      </main>

      <Partners />
      <Footer />
    </div>
  );
}